/**
 * Merchants page: template MUI components + API.
 * Table of merchants; row click opens detail drawer. Create → /merchant/new.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import MerchantDetailDrawer from './MerchantDetailDrawer';
import DataGridTableToolbar from '../components/DataGridTableToolbar';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';

function getMerchantId(m) {
  return m?.email ?? m?.login ?? m?.id ?? null;
}

function mapMerchantsToRows(list) {
  if (!Array.isArray(list)) return [];
  return list.map((m, index) => {
    const id = getMerchantId(m) || `row-${index}`;
    const email = m.email ?? m.login ?? '—';
    const locked = Boolean(m.lock ?? m.locked ?? m.blocking);
    const partnerEmail = m.partnerEmail ?? m.partner ?? '';
    return {
      id: String(id),
      email,
      partnerEmail,
      locked,
      _raw: m,
    };
  });
}

export default function MerchantsPage() {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const role = typeof localStorage !== 'undefined' ? localStorage.getItem('role') || '' : '';
  const adminEmail = typeof localStorage !== 'undefined' ? localStorage.getItem('Email') || '' : '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState(null);

  const loadMerchants = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const promise = role === 'SUPER_ADMIN' ? api.getMerchants(true) : api.getMerchantsBy(adminEmail);
      const { data } = await promise;
      const list = Array.isArray(data) ? data : (data?.data != null ? (Array.isArray(data.data) ? data.data : [data.data]) : []);
      setRows(mapMerchantsToRows(list));
    } catch (e) {
      setError(e?.message ?? t('merchants.errorLoad'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [role, adminEmail, t]);

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  useEffect(() => {
    if (loading || rows.length === 0) return undefined;
    let email = '';
    try {
      email = (sessionStorage.getItem('crossLinkMerchant') || '').trim();
      if (email) sessionStorage.removeItem('crossLinkMerchant');
    } catch (_) { /* ignore */ }
    if (!email) return undefined;
    setSearchInputValue(email);
    setSearchQuery(email);
    const hit = rows.find((r) => (r.email || '').toLowerCase() === email.toLowerCase());
    if (hit) {
      setSelectedMerchant(hit._raw);
      setDetailOpen(true);
    }
    return undefined;
  }, [loading, rows]);

  const filteredRows = useMemo(() => {
    const q = (searchQuery ?? searchInputValue ?? '').toString().trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      (row.email || '').toLowerCase().includes(q)
      || (row.partnerEmail || '').toLowerCase().includes(q)
    );
  }, [rows, searchQuery, searchInputValue]);

  const handleRefresh = useCallback(() => {
    if (!loading) {
      setSearchQuery(null);
      setSearchInputValue('');
      loadMerchants();
    }
  }, [loading, loadMerchants]);

  const handleSearch = useCallback((query) => {
    const q = (query ?? searchInputValue ?? '').toString().trim();
    setSearchQuery(q || null);
    setSearchInputValue(q);
  }, [searchInputValue]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery(null);
    setSearchInputValue('');
    setError(null);
    loadMerchants();
  }, [loadMerchants]);

  const handleRowClick = useCallback((params) => {
    const full = params.row._raw ?? params.row;
    setSelectedMerchant(full);
    setDetailOpen(true);
  }, []);

  const columns = useMemo(
    () => {
      const cols = [
        { field: 'email', headerName: t('merchants.columnEmail'), flex: 1, minWidth: 220 },
      ];
      if (role === 'SUPER_ADMIN') {
        cols.push({
          field: 'partnerEmail',
          headerName: t('merchants.columnPartner'),
          flex: 1,
          minWidth: 200,
          valueGetter: (value, row) => row?.partnerEmail || '—',
        });
      }
      cols.push({
        field: 'locked',
        headerName: t('merchants.columnStatus'),
        flex: 0.4,
        minWidth: 100,
        renderCell: (params) => (
          <Chip
            label={params.value ? t('merchants.blocked') : t('merchants.active')}
            color={params.value ? 'error' : 'success'}
            size="small"
          />
        ),
      });
      return cols;
    },
    [t, role]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageContainer
        title={t('merchants.title')}
        breadcrumbs={[]}
        actions={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              component="a"
              href="#/merchant/new"
            >
              {t('merchants.create')}
            </Button>
            {searchQuery != null && (
              <Chip label={t('common.clearSearch')} onDelete={handleClearSearch} size="small" sx={{ mr: 0.5 }} />
            )}
            <Tooltip title={t('common.reloadData')} placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        }
      >
        <Box sx={{ flex: 1, width: '100%' }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            <DataGrid
              rows={filteredRows}
              columns={columns}
              loading={loading}
              rowCount={filteredRows.length}
              paginationMode="client"
              pageSizeOptions={[10, 20, 50]}
              onRowClick={handleRowClick}
              localeText={dataGridLocaleText}
              getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
              initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
              disableColumnResize
              density="compact"
              showToolbar
              slots={{ toolbar: DataGridTableToolbar }}
              slotProps={{
                toolbar: {
                  searchValue: searchInputValue,
                  onSearchChange: setSearchInputValue,
                  onSearchSubmit: handleSearch,
                  searchPlaceholder: t('merchants.searchPlaceholder'),
                  loading,
                },
                loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' },
                baseIconButton: { size: 'small' },
              }}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                [`& .${gridClasses.row}`]: { cursor: 'pointer' },
              }}
            />
          )}
        </Box>
      </PageContainer>
      <MerchantDetailDrawer
        merchant={selectedMerchant}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedMerchant(null); }}
        onRefresh={loadMerchants}
      />
    </Box>
  );
}
